import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, BehaviorSubject } from 'rxjs';
import { tap, map } from 'rxjs/operators';
import { environment } from '../../environments/environment';

export interface Locale {
  code: string;
  name: string;
}

export interface Country {
  code: string;
  name: string;
  tax_rate: string;
  locales: string[];
}

export interface LocalesResponse {
  locales: Locale[];
  countries: Country[];
}

const STORAGE_KEY_LOCALE = 'podlunsek_locale';
const STORAGE_KEY_COUNTRY = 'podlunsek_country';

@Injectable({
  providedIn: 'root'
})
export class LanguageService {
  private localesData: LocalesResponse | null = null;
  private localesLoaded = new BehaviorSubject<boolean>(false);

  public selectedLocale: string | null = null;
  public selectedCountry: string | null = null;

  constructor(
    private http: HttpClient
  ) {
    this.loadFromStorage();
  }

  getLocales(): Observable<LocalesResponse> {
    const headers = new HttpHeaders({'Auth-token': 'hsd76bk0c6D6ac73s7D7fE4hF3b3a13cDf53Shbs1'});

    return this.http.get<LocalesResponse>(`${environment.apiUrl}configuration/locales`, {headers}).pipe(
      tap(data => {
        this.localesData = data;
        this.localesLoaded.next(true);
      })
    );
  }

  getLocalesData(): LocalesResponse | null {
    return this.localesData;
  }

  isLocalesLoaded(): Observable<boolean> {
    return this.localesLoaded.asObservable();
  }

  loadFromStorage(): void {
    const storedLocale = localStorage.getItem(STORAGE_KEY_LOCALE);
    const storedCountry = localStorage.getItem(STORAGE_KEY_COUNTRY);

    if (storedLocale) {
      this.selectedLocale = storedLocale;
    }
    if (storedCountry) {
      this.selectedCountry = storedCountry;
    }
  }

  saveToStorage(): void {
    if (this.selectedLocale) {
      localStorage.setItem(STORAGE_KEY_LOCALE, this.selectedLocale);
    }
    if (this.selectedCountry) {
      localStorage.setItem(STORAGE_KEY_COUNTRY, this.selectedCountry);
    }
  }

  clearStorage(): void {
    localStorage.removeItem(STORAGE_KEY_LOCALE);
    localStorage.removeItem(STORAGE_KEY_COUNTRY);
    this.selectedLocale = null;
    this.selectedCountry = null;
  }

  setLocaleAndCountry(locale: string, country: string): void {
    this.selectedLocale = locale;
    this.selectedCountry = country;
    this.saveToStorage();
  }

  hasValidSelection(): boolean {
    return !!this.selectedLocale && !!this.selectedCountry;
  }

  validateCountryLocale(country: string, locale: string): Observable<boolean> {
    return this.getLocales().pipe(
      map(data => {
        const countryData = data.countries.find(c => c.code === country);
        if (!countryData) {
          return false;
        }
        return countryData.locales.includes(locale);
      })
    );
  }

  validateCountryLocaleSync(country: string, locale: string): boolean {
    if (!this.localesData) {
      return false;
    }
    const countryData = this.localesData.countries.find(c => c.code === country);
    if (!countryData) {
      return false;
    }
    return countryData.locales.includes(locale);
  }

  getAvailableLocalesForCountry(countryCode: string): Locale[] {
    if (!this.localesData) {
      return [];
    }
    const country = this.localesData.countries.find(c => c.code === countryCode);
    if (!country) {
      return [];
    }
    return this.localesData.locales.filter(l => country.locales.includes(l.code));
  }
}
